import SortIcon from "app/components/Icons/SortIcon";
import CustomTooltip from "app/components/Tooltip";
import { sortOrder } from "app/types";
import clsx from "clsx";
import React, { useRef } from "react";
import { ColumnHeadersProps } from "./types";

const ColumnHeaders: React.FC<ColumnHeadersProps> = ({
  gridTemplate,
  minTableWidth,
  cols,
  sortByColumn,
  sortByOrder,
  handleSort,
  setCols,
  onCommitColumnWidths,
}) => {
  const latestColsRef = React.useRef(cols);

  /* ---------- Resize logic ---------- */
  const resizing = useRef<{
    key: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  const onResizeStart = (key: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const col = cols.find((c) => c.id === key)!;
    latestColsRef.current = cols;
    resizing.current = {
      key,
      startX: e.clientX,
      startWidth: Number(col.width),
    };
    window.addEventListener("mousemove", onResizing);
    window.addEventListener("mouseup", onResizeEnd, { once: true });
  };
  const onResizing = (ev: MouseEvent) => {
    const info = resizing.current;
    if (!info) return;
    const dx = ev.clientX - info.startX;
    setCols((prev) => {
      const next = prev.map((c) =>
        c.id === info.key
          ? {
              ...c,
              width: Math.min(
                Number(c.max),
                Math.max(Number(c.min), info.startWidth + dx),
              ),
            }
          : c,
      );
      latestColsRef.current = next;
      return next;
    });
  };
  const onResizeEnd = () => {
    window.removeEventListener("mousemove", onResizing);
    const info = resizing.current;
    resizing.current = null;
    if (!info || !onCommitColumnWidths) return;
    const finalCols = latestColsRef.current;
    const resized = finalCols.find((c) => c.id === info.key);
    if (!resized || Number(resized.width) === info.startWidth) return;
    onCommitColumnWidths(finalCols);
  };

  return (
    <div
      className="grid items-center  text-[12px] text-gray-500 bg-gray-400  min-h-[30px] h-[30px]  "
      role="row"
      style={{
        gridTemplateColumns: gridTemplate,
        minWidth: minTableWidth,
      }}
    >
      {cols.map((c, i) => (
        <div
          key={c.id}
          className={clsx(
            "relative select-none bg-gray-400 p-[4px] hover:bg-gray-100 h-full group border-b border-gray-100  ",
            i === 0 && "sticky left-[62px] z-20",
          )}
        >
          {i === 0 && (
            <div
              className={` flex items-center absolute -left-[62px] top-0 w-[62px] h-full bg-gray-400 min-h-[30px] h-[30px] `}
            ></div>
          )}

          <div className="flex items-center text-xs font-medium text-secondary w-full gap-[2px] h-full ">
            <span className="truncate">{c.name}</span>
            {c.sortable && (
              <button
                onClick={() => handleSort(c?.id)}
                className={`opacity-0 group-hover:opacity-100  ${
                  sortByColumn === c.id ? "opacity-100" : ""
                }`}
              >
                <CustomTooltip text="Sort" className="w-full ">
                  <button
                    className={`transition-transform ${
                      sortByColumn === c.id && sortByOrder === sortOrder.DESC
                        ? "rotate-180"
                        : ""
                    }`}
                  >
                    <SortIcon />
                  </button>
                </CustomTooltip>
              </button>
            )}
          </div>

          {/* resize handle */}
          {c.resizable !== false && (
            <div
              className={clsx(
                "absolute right-0 top-1/2 -translate-y-1/2 h-[24px] w-[4px] cursor-col-resize bg-[#5A8DF1] rounded opacity-0 hover:opacity-100 transition-opacity duration-200 z-10",
                i === cols.length - 1 && "right-[-2px]",
              )}
              onMouseDown={(e) => onResizeStart(c.id, e)}
              title="Drag to resize"
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default ColumnHeaders;
